import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Share2, Star, Shield, Truck, RotateCcw, ChevronLeft, ChevronRight, Minus, Plus, ShoppingCart, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { ProductCard } from './ProductCard';
import { LoadingSpinner } from './LoadingSpinner';
import { Product } from '../types';
import { API_PATHS } from '../constants/api-constants';
import { formatWeight, formatPurity } from '../lib/utils';

interface ProductDetailPageProps {}

export function ProductDetailPage({}: ProductDetailPageProps) {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  useEffect(() => {
    const loadProduct = async () => {
      if (!slug) return;
      setLoading(true);
      try {
        const response = await fetch(API_PATHS.FETCH_PRODUCT_BY_SLUG(slug));
        if (response.ok) {
          const data = await response.json();
          setProduct(data);
        } else {
          console.error('Product not found');
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
    setCurrentImageIndex(0);
    setQuantity(1);
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[600px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[600px] text-center p-6">
        <h2 className="text-2xl mb-4">Product Not Found</h2>
        <p className="text-muted-foreground mb-6">The product you are looking for might have been moved or deleted.</p>
        <Button onClick={() => navigate('/')} className="bg-brand-green text-white">
          Back to Home
        </Button>
      </div>
    );
  }

  const isFavorite = false; // Favorites functionality can be restored here
  const images = Array.isArray(product.images) ? product.images : [];
  const discountPercentage = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleAddToCart = async () => {
    setIsAddingToCart(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    // onAddToCart functionality can be added here
    setIsAddingToCart(false);
  };

  const handleBuyNow = () => {
    const message = `Hi, I am interested in buying: ${product.name}`;
    const url = `https://wa.me/918867458165?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const getMaterial = (category?: string) => {
    if (!category) return '-';
    return category === 'gold' ? 'Gold' : category.charAt(0).toUpperCase() + category.slice(1);
  };

  const getPurity = (category?: string, purity?: string) => {
    if (purity) return formatPurity(purity, category);
    if (category === 'gold') return '22 Karat';
    if (category === 'silver') return '925';
    return '-';
  };

  const getWeight = (weight?: string) => formatWeight(weight) || '-';

  const details = {
    description: product.description || `Beautiful ${product.category} jewellery piece crafted with premium quality materials.`,
    features: (product.features && product.features.length > 0) ? product.features : [
      'Premium quality materials',
      'Traditional craftsmanship',
      'Certificate of authenticity included',
      'Lifetime manufacturing warranty'
    ],
    specifications: {
      'Material': getMaterial(product.category),
      'Purity': getPurity(product.category, product.purity),
      'Weight': getWeight(product.weight),
      'Origin': product?.origin || 'India',
      'Certification': product?.certification || 'BIS Certified'
    }
  };

  const similarProducts: Product[] = []; // Similar products can be fetched here if needed

  return (
    <div className="min-h-screen bg-focused-bg transition-colors duration-300">
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-border">
        <div className="flex items-center justify-between px-6 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="hover:bg-brand-green/10"
          >
            <ArrowLeft className="h-4 w-4 mr-2 text-brand-green" />
            Back
          </Button>

          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {}}
              className="hover:bg-brand-green/10 text-muted-foreground"
            >
              <Heart className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="icon" className="hover:bg-brand-green/10">
              <Share2 className="h-5 w-5 text-brand-green" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-4">
            <div className="relative aspect-square bg-white rounded-lg overflow-hidden border border-border">
              <ImageWithFallback
                src={images[currentImageIndex]}
                alt={product.name}
                className="w-full h-full object-cover"
              />

              {images.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white"
                    onClick={prevImage}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white"
                    onClick={nextImage}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </>
              )}

              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {product.isNew && (
                  <Badge className="bg-brand-green hover:bg-brand-green/90 text-white">New</Badge>
                )}
                {product.isBestseller && (
                  <Badge variant="secondary" className="bg-brand-gold/20 text-brand-gold border-brand-gold/30">Bestseller</Badge>
                )}
                {/* {discountPercentage > 0 && (
                  <Badge variant="destructive">{discountPercentage}% OFF</Badge>
                )} */}
              </div>
            </div>

            {images.length > 1 && (
              <div className="flex space-x-2 overflow-x-auto">
                {images.map((image, index) => (
                  <button
                    key={index}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${index === currentImageIndex ? 'border-brand-green' : 'border-border'}`}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <ImageWithFallback src={image} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div>
              <h1 className="text-3xl mb-2">{product.name}</h1>
              <div className="flex items-center space-x-4 mb-4">
                {/* <div className="flex items-center space-x-1">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  <span>{product.rating}</span>
                  <span className="text-muted-foreground">({product.reviewCount} reviews)</span>
                </div> */}
                <Badge variant="outline" className="border-brand-green/30 text-brand-green">
                  {product.category.charAt(0).toUpperCase() + product.category.slice(1)}
                </Badge>
              </div>

              {/* <div className="flex items-center space-x-4 mb-6">
                <span className="text-3xl text-brand-green">₹{product.price.toLocaleString()}</span>
                {product.originalPrice && (
                  <span className="text-xl text-muted-foreground line-through">₹{product.originalPrice.toLocaleString()}</span>
                )}
              </div> */}
            </div>

            <Card className="border-brand-green/20">
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-4">
                  {product.weight && (
                    <div>
                      <p className="text-sm text-muted-foreground">Weight</p>
                      <p className="font-medium">{product.weight}</p>
                    </div>
                  )}
                  {product.purity && (
                    <div>
                      <p className="text-sm text-muted-foreground">Purity</p>
                      <p className="font-medium">{product.purity}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center space-x-4">
              <span>Quantity:</span>
              <div className="flex items-center border border-border rounded-lg">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 hover:bg-brand-green/10"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="px-4 py-2 min-w-12 text-center">{quantity}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 hover:bg-brand-green/10"
                  onClick={() => setQuantity(quantity + 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              {/* <Button
                className="flex-1 bg-brand-green hover:bg-brand-green/90 text-white"
                onClick={handleAddToCart}
                disabled={isAddingToCart}
              >
                {isAddingToCart ? <LoadingSpinner size="sm" className="mr-2" /> : <ShoppingCart className="h-4 w-4 mr-2" />}
                Add to Cart
              </Button> */}
              <Button
                className="flex-1 bg-brand-green hover:bg-brand-green/90 text-white py-6 text-lg"
                onClick={handleBuyNow}
              >
                <Zap className="h-5 w-5 mr-2" />
                Buy Now
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
              <div className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-brand-green" />
                <div>
                  <p className="text-sm font-medium">Certified</p>
                  <p className="text-xs text-muted-foreground">BIS Hallmarked</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <Tabs defaultValue="description" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="description">Description</TabsTrigger>
              <TabsTrigger value="specifications">Specifications</TabsTrigger>
              {/* <TabsTrigger value="reviews">Reviews</TabsTrigger> */}
            </TabsList>

            <TabsContent value="description" className="mt-6">
              <Card>
                <CardContent className="p-6">
                  <p className="text-muted-foreground mb-4">{details.description}</p>
                  <h4 className="mb-3">Key Features:</h4>
                  <ul className="space-y-2">
                    {details.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <div className="h-2 w-2 bg-brand-green rounded-full mt-2" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="specifications" className="mt-6">
              <Card>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(details.specifications).map(([key, value]) => (
                      <div key={key} className="flex justify-between py-2 border-b border-border last:border-0">
                        <span className="font-medium">{key}:</span>
                        <span className="text-muted-foreground">{value}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* <TabsContent value="reviews" className="mt-6">
              <Card>
                <CardContent className="p-6">
                  <div className="text-center py-8">
                    <Star className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground">Reviews feature coming soon</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent> */}
          </Tabs>
        </div>

        {similarProducts.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl mb-6">Similar Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {similarProducts.map((similarProduct) => (
                <ProductCard
                  key={similarProduct.id}
                  product={similarProduct}
                  onAddToCart={() => {}}
                  onToggleFavorite={() => {}}
                  isFavorite={false}
                  onProductClick={(p) => navigate(`/product/${p.slug}`)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}